import { ComponentConfig, FieldLabel } from '@measured/puck';
import { v4 as uuidv4 } from 'uuid';
import {
  PaddingProps,
  PaddingType,
  paddingFields,
} from '../../../puck/reusable-props/padding';
import { ReduxSelectorProps } from '../../../services/constants';
import CustomTooltip from '../../../../components/common/tooltip';
import Button from '../../../../components/common/button';
import showComponent, {
  showComponentProps,
} from '../../../puck/reusable-props/showComponent';
import {
  getBorderProps,
  getcurrentDeviceType,
  getNewMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
} from '../../../utils/helper';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../../puck/reusable-props/Border';
import { FAQ } from './FAQDisclosureSection';
import { backgroungFields } from '../../../puck/reusable-props/backgroungFields';
import QuillFieldRenderer from '../../carousel review/QuillFieldRenderer';
import '../../text-editor/style.scss';
import ColorPicker from '../../../puck/reusable-props/ColorPicker';
import PaddingComponent from '../../customComponents/PaddingComponent';
import WithAccordion from '../../customComponents/CustomAccordion';
import React from 'react';
import NewMarginFiled from '../../../puck/reusable-props/newMarginField';
import {
  marginFields,
  MarginProps,
  MarginType,
} from '../../../puck/reusable-props/NewMargin';
import { Switch } from '@mui/material';
import { QuillEditor } from '../../../../components/common/quill-editor/quill-editor';

export type FAQItem = {
  id: string;
  Title: string;
  Description: string;
};

export type FAQDisclosureSectionProps = MarginProps &
  PaddingProps &
  showComponentProps & {
    customHeader: {
      showHeaderDivider: boolean;
      header: string;
    };
    faqItems: FAQItem[];
    defaultCollapse: boolean;
    FAQdivider: {
      showDivider: boolean;
      color: string;
      style: string;
      thickness: number;
    };
    border: IBorderValueProps;
    FAQborder: IBorderValueProps;
    FAQmargin: MarginType;
    FAQpadding: PaddingType;
    FAQbackground: {
      titleBgColor: string;
      descriptionBgColor: string;
    };
    background: {
      color: string;
      isInherit: boolean;
    };
    dropdownIcon: {
      showIcon: boolean;
      iconColor: string;
      iconSize: number;
    };
  };

export const FAQDisclosureSection = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<FAQDisclosureSectionProps> => {
  return {
    label: (
      <CustomTooltip title="FAQDisclosureSection">
        <span>FAQ Disclosure Section</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      customHeader: {
        type: 'custom',
        label: 'Header',
        render: ({ value, onChange }) => {
          return (
            <WithAccordion title="Header">
              <QuillEditor
                value={value.header || ''}
                onChange={val =>
                  onChange({
                    ...value,
                    header: val,
                  })
                }
                id={1}
              />
              <div className="flex gap-2 items-center justify-between">
                <label>Show Header Divider</label>
                <Switch
                  checked={value?.showHeaderDivider}
                  onChange={e =>
                    onChange({
                      ...value,
                      showHeaderDivider: e.target.checked,
                    })
                  }
                  className="mr-2 !appearance-auto cursor-pointer"
                />
              </div>
            </WithAccordion>
          );
        },
      },
      faqItems: {
        type: 'custom',
        label: 'FAQ Items',
        render: ({ value, onChange }) => {
          value = value || [];

          const handleAddItem = () => {
            const newItem: FAQItem = {
              id: uuidv4(),
              Title:
                '<p style="line-height: 1.5;"><strong style="font-size: 18px;">New Question</strong></p>',
              Description:
                '<p style="line-height:1.5;"><span style="font-size: 14px;">Answer to the question goes here.</span></p>',
            };
            onChange([...value, newItem]);
          };

          const handleEditItem = <K extends keyof FAQItem>(
            index: number,
            key: K,
            newValue: FAQItem[K],
          ) => {
            onChange(
              value.map((item, i) =>
                i === index ? { ...item, [key]: newValue } : item,
              ),
            );
          };

          const handleRemoveItem = (index: number) => {
            onChange(value.filter((_: unknown, i: number) => i !== index));
          };
          return (
            <div>
              <Button
                label="+ Add new FAQ"
                className="mt-4 w-full"
                variant="phoenix"
                onClick={handleAddItem}
              />
              {value.map((item: FAQItem, index: number) => (
                <div
                  key={item.id || `faq-item-${index}`}
                  className="mt-4 p-4 border border-gray-200 rounded">
                  {/* Using QuillFieldRenderer for Question/Title */}
                  <QuillFieldRenderer
                    label="Question"
                    value={item?.Title || ''}
                    onChange={val => handleEditItem(index, 'Title', val || '')}
                    id={2 + index}
                  />

                  {/* Using QuillFieldRenderer for Answer/Description */}
                  <QuillFieldRenderer
                    label="Answer"
                    value={item?.Description || ''}
                    onChange={val =>
                      handleEditItem(index, 'Description', val || '')
                    }
                    id={15 + index}
                  />

                  <Button
                    label="- Remove FAQ"
                    className="mt-4 w-full"
                    variant="phoenix_secondary"
                    onClick={() => handleRemoveItem(index)}
                  />
                </div>
              ))}
            </div>
          );
        },
      },
      defaultCollapse: {
        type: 'radio',
        label: 'Default Collapse',
        options: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
        ],
      },
      FAQdivider: {
        type: 'custom',
        label: 'FAQ Divider',
        render: ({ value, onChange }) => {
          return (
            <WithAccordion title="FAQ Divider">
              <>
                <div className="mb-4 mt-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={value?.showDivider}
                      onChange={e =>
                        onChange({
                          ...value,
                          showDivider: e.target.checked,
                        })
                      }
                      className="mr-2 !appearance-auto cursor-pointer"
                    />
                    <span className="text-sm">Show Divider</span>
                  </label>
                </div>
                <div className="mb-4">
                  <ColorPicker
                    value={value?.color || ''}
                    onChange={color =>
                      onChange({
                        ...value,
                        color: color,
                      })
                    }
                    label={'Divider Color'}
                    tooltip={'dividerColor'}
                  />
                </div>
                <div className="mb-4">
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip title="dividerThickness">
                          <span>Divider Thickness</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }>
                    <input
                      type="number"
                      value={value?.thickness || 1}
                      onChange={e =>
                        onChange({
                          ...value,
                          thickness: Number(e.target.value),
                        })
                      }
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                  </FieldLabel>
                </div>

                <div className="mb-4">
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip title="dividerStyle">
                          <span>Divider Style</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }>
                    <select
                      value={value?.style || 'solid'}
                      onChange={e =>
                        onChange({
                          ...value,
                          style: e.target.value,
                        })
                      }
                      className="w-full border rounded px-2 py-1 text-sm">
                      <option value="solid">Solid</option>
                      <option value="dashed">Dashed</option>
                      <option value="dotted">Dotted</option>
                    </select>
                  </FieldLabel>
                </div>
              </>
            </WithAccordion>
          );
        },
      },
      FAQborder: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="FAQ Border" />
        ),
      },
      FAQmargin: {
        type: 'custom',
        label: 'FAQ Margin',
        render: ({ value, onChange }) => (
          <NewMarginFiled
            value={value}
            onChange={onChange}
            title="FAQ Margin"
          />
        ),
      },
      FAQpadding: {
        type: 'custom',
        label: 'FAQ Padding',
        render: ({ value, onChange }) => (
          <PaddingComponent
            value={value}
            onChange={onChange}
            title="FAQ Padding"
          />
        ),
      },
      FAQbackground: {
        type: 'custom',
        label: 'FAQ Background',
        render: ({ value, onChange }) => (
          <WithAccordion title="FAQ Background">
            <>
              <ColorPicker
                value={value?.titleBgColor || ''}
                onChange={color =>
                  onChange({
                    ...value,
                    titleBgColor: color,
                  })
                }
                label={'Title Background Color'}
                tooltip={'titleBackgroundColor'}
              />
              <ColorPicker
                value={value?.descriptionBgColor || ''}
                onChange={color =>
                  onChange({
                    ...value,
                    descriptionBgColor: color,
                  })
                }
                label={'Description Background Color'}
                tooltip={'descriptionBackgroundColor'}
              />
            </>
          </WithAccordion>
        ),
      },

      dropdownIcon: {
        type: 'custom',
        label: 'Dropdown Icon',
        render: ({ value, onChange }) => {
          return (
            <WithAccordion title="Dropdown Icon">
              <>
                <ColorPicker
                  value={value?.iconColor}
                  onChange={color =>
                    onChange({
                      ...value,
                      iconColor: color,
                    })
                  }
                  label={'Icon Color'}
                  tooltip={'iconColor'}
                />
                <div className="mb-4 mt-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={value?.showIcon}
                      onChange={e =>
                        onChange({
                          ...value,
                          showIcon: e.target.checked,
                        })
                      }
                      className="mr-2 !appearance-auto cursor-pointer"
                    />
                    <span className="text-sm">Show Icon</span>
                  </label>
                </div>
                <div className="mb-4">
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip title="iconSize">
                          <span>Icon Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }>
                    <input
                      type="number"
                      value={value?.iconSize || 16}
                      onChange={e =>
                        onChange({
                          ...value,
                          iconSize: Number(e.target.value),
                        })
                      }
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                  </FieldLabel>
                </div>
              </>
            </WithAccordion>
          );
        },
      },
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      ...backgroungFields,
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      customHeader: {
        showHeaderDivider: true,
        header:
          '<h3 style="line-height:1; style="font-size: 20px;"><span>Header Content</span></h3>',
      },
      faqItems: [
        {
          id: uuidv4(),
          Title:
            '<p style="line-height: 1.5;"><strong style="font-size: 18px;">New Question</strong></p>',
          Description:
            '<p style="line-height:1.5;"><span style="font-size: 14px;">New Description</span></p>',
        },
      ],
      defaultCollapse: false,
      FAQdivider: {
        showDivider: true,
        color: '#000000',
        style: 'solid',
        thickness: 1,
      },
      dropdownIcon: {
        showIcon: true,
        iconColor: '#000000',
        iconSize: 18,
      },
      margin: {
        deviceType: 'desktop',
        marginBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      FAQmargin: {
        deviceType: 'desktop',
        marginBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      FAQpadding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      ...borderProps,
      FAQborder: borderProps.border,
      FAQbackground: {
        titleBgColor: '#FFFFFF',
        descriptionBgColor: '#FFFFFF',
      },

      background: {
        color: '#FFFFFF',
        isInherit: false,
      },
    },
    render: ({
      margin,
      padding,
      faqItems,
      defaultCollapse,
      FAQdivider,
      customHeader,
      showComponent,
      editMode,
      border,
      FAQpadding,
      FAQborder,
      FAQbackground,
      FAQmargin,
      background,
      dropdownIcon,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getNewMarginStyles(margin, currentDeviceType);
      const state = useAppSelector(state => state);
      return (
        <>
          <div className="faq-disclosure-section">
            {showComponent?.includes(currentDeviceType) && (
              <div
                style={{
                  ...marginStyles,
                  ...getPaddingStyles(padding, currentDeviceType),
                  ...getBorderProps(border, currentDeviceType),
                  backgroundColor: background?.isInherit
                    ? 'inherit'
                    : background?.color,
                }}
                className="w-full">
                <div className="flex items-center justify-center mb-5">
                  {customHeader.showHeaderDivider && (
                    <div className="flex-grow border-b border-black mx-2" />
                  )}
                  <div
                    className="text-editor-content text-left"
                    style={{ lineHeight: 1.5 }}
                    dangerouslySetInnerHTML={{ __html: customHeader.header }}
                  />
                  {customHeader.showHeaderDivider && (
                    <div className="flex-grow border-b border-black mx-2" />
                  )}
                </div>

                <div className="faq-container">
                  {faqItems.map((item, index) => (
                    <div key={item?.id || `faq-${index}`} className="mb-3">
                      <FAQ
                        descriptionBgColor={
                          FAQbackground.descriptionBgColor || ''
                        }
                        titleBgColor={FAQbackground.titleBgColor || ''}
                        FAQborder={FAQborder}
                        FAQmargin={FAQmargin}
                        FAQpadding={FAQpadding}
                        currentDeviceType={currentDeviceType}
                        Title={getQuillEditorText(item?.Title || '', state)}
                        Description={getQuillEditorText(
                          item?.Description || '',
                          state,
                        )}
                        open={defaultCollapse}
                        dropdownIcon={dropdownIcon}
                      />
                      {FAQdivider.showDivider && (
                        <div
                          style={{
                            borderBottom: `${FAQdivider.thickness || 1}px ${FAQdivider.style || 'solid'} ${FAQdivider.color || '#000000'}`,
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      );
    },
  };
};
